import React from 'react';
import Link from 'next/link';
import { ArrowRight, Hash } from 'lucide-react';
import type { Category } from '@/data/categories';

interface CategoryCardProps {
  category: Category;
  variant?: 'default' | 'compact' | 'hero';
}

export default function CategoryCard({ category, variant = 'default' }: CategoryCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        href={`/categories/${category.slug}`}
        className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary hover:bg-accent transition-all duration-150 group"
      >
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors truncate">
            {category.name}
          </p>
          <p className="text-xs text-muted-foreground">{category.promptCount} prompts</p>
        </div>
        <ArrowRight size={14} className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
      </Link>
    );
  }

  if (variant === 'hero') {
    return (
      <Link
        href={`/categories/${category.slug}`}
        className="group rounded-2xl aspect-[3/2] block border border-border bg-gradient-to-br from-accent via-white to-pink-50 p-5"
        aria-label={`Browse ${category.name} prompts — ${category.promptCount} prompts available`}
      >
        <div className="flex h-full flex-col justify-between">
          <div className="flex items-start justify-between gap-3">
            <span className="rounded-full bg-white/80 px-2.5 py-1 text-xs font-medium text-muted-foreground border border-border">
              {category.group}
            </span>
          </div>
          <div>
            <p className="font-bold text-lg leading-tight text-foreground group-hover:text-primary transition-colors">
              {category.name}
            </p>
            <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{category.description}</p>
            <p className="text-xs mt-3 text-primary font-semibold">{category.promptCount} prompts</p>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={`/categories/${category.slug}`}
      className="card-base group flex flex-col p-4"
      aria-label={`Browse ${category.name} AI image prompts`}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <span className="badge-muted text-[11px]">
          {category.group}
        </span>
      </div>
      <div className="flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm leading-tight">
          {category.name}
          </h3>
          <span className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
            <Hash size={11} />
            {category.promptCount}
          </span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
          {category.description}
        </p>
        <div className="flex items-center gap-1 text-primary text-xs font-semibold mt-auto pt-1">
          Browse prompts
          <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
